import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { SharedTask, SharedTaskStatus } from '@/types';
import { getUserColor } from '@/utils/calendarHelpers';

const PRIORITY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  urgent: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Urgent' },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'High' },
  normal: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Normal' },
  low: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Low' },
};

const CATEGORY_LABELS: Record<string, string> = {
  household: 'Household',
  kai_care: "Kai's Care",
  shopping: 'Shopping',
  medical: 'Medical',
  general: 'General',
};

const STATUS_CYCLE: Record<SharedTaskStatus, SharedTaskStatus> = {
  todo: 'in_progress',
  in_progress: 'done',
  done: 'todo',
};

interface TaskItemProps {
  task: SharedTask;
}

export function TaskItem({ task }: TaskItemProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const priority = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.normal;

  const updateTask = useMutation({
    mutationFn: async (data: Partial<SharedTask>) => {
      return api.patch(`/shared-tasks/${task.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['shared-tasks-counts'] });
    },
  });

  const deleteTask = useMutation({
    mutationFn: async () => {
      return api.delete(`/shared-tasks/${task.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['shared-tasks-counts'] });
    },
  });

  const handleStatusCycle = () => {
    const nextStatus = STATUS_CYCLE[task.status];
    updateTask.mutate({ status: nextStatus } as any);
  };

  const isOverdue = task.due_date && task.status !== 'done' && task.due_date < format(new Date(), 'yyyy-MM-dd');
  const canDelete = user?.id === task.created_by.id || user?.role === 'parent';

  return (
    <div className={`bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden transition-all ${task.status === 'done' ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3 p-3">
        {/* Status checkbox */}
        <button
          onClick={handleStatusCycle}
          disabled={updateTask.isPending}
          className="mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
          style={{
            borderColor: task.status === 'done' ? '#10b981' : task.status === 'in_progress' ? '#3b82f6' : '#4b5563',
            backgroundColor: task.status === 'done' ? '#10b98120' : task.status === 'in_progress' ? '#3b82f620' : 'transparent',
          }}
          title={`Status: ${task.status} — click to cycle`}
        >
          {task.status === 'done' && (
            <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {task.status === 'in_progress' && (
            <div className="w-2 h-2 rounded-full bg-blue-400" />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm text-white ${task.status === 'done' ? 'line-through text-gray-400' : ''}`}>
              {task.title}
            </p>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-0.5 text-gray-500 hover:text-gray-300 flex-shrink-0"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {task.priority !== 'normal' && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${priority.bg} ${priority.text}`}>
                {priority.label}
              </span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-gray-400">
              {CATEGORY_LABELS[task.category] || task.category}
            </span>
            {task.assigned_to && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5"
                style={{ backgroundColor: `${getUserColor(task.assigned_to)}20`, color: getUserColor(task.assigned_to, '#9ca3af') }}
              >
                {task.assigned_to.emoji && <span>{task.assigned_to.emoji}</span>}
                {task.assigned_to.first_name}
              </span>
            )}
            {task.due_date && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isOverdue ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-gray-400'}`}>
                {isOverdue ? 'Overdue: ' : ''}{format(new Date(task.due_date + 'T12:00:00'), 'MMM d')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 space-y-2 border-t border-slate-700/50 mt-1 pt-2">
          {task.notes && (
            <p className="text-xs text-gray-400">{task.notes}</p>
          )}
          <div className="flex items-center justify-between text-[10px] text-gray-500">
            <span>
              Added by {task.created_by.emoji} {task.created_by.first_name}
              {task.completed_by && ` · Done by ${task.completed_by.emoji} ${task.completed_by.first_name}`}
            </span>
            {canDelete && (
              <button
                onClick={() => deleteTask.mutate()}
                disabled={deleteTask.isPending}
                className="p-1 text-gray-500 hover:text-red-400 transition-colors"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
